import { FormikErrors } from 'formik';

import { PathSelector } from '@/react/portainer/gitops/ComposePathField/PathSelector';
import {
  GitFormModel,
  RelativePathModel,
} from '@/react/portainer/gitops/types';
import { getPerDevConfigsFilterType } from '@/react/portainer/gitops/RelativePathFieldset/types';

import { FormControl } from '@@/form-components/FormControl';
import { TextTip } from '@@/Tip/TextTip';
import { InsightsBox } from '@@/InsightsBox';
import { SwitchField } from '@@/form-components/SwitchField';
import { Select } from '@@/form-components/Input';

import { FormValues } from '../types';

interface Props {
  value: RelativePathModel;
  gitModel: GitFormModel;
  errors: FormikErrors<FormValues>;
  onChange: (value: RelativePathModel) => void;
}

export function HelmPerDeviceConfigSection({
  value,
  gitModel,
  errors,
  onChange,
}: Props) {
  function handleChange(updates: Partial<RelativePathModel>) {
    onChange({
      ...value,
      ...updates,
    });
  }

  return (
    <>
      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            name="EnablePerDeviceConfigs"
            data-cy="gitops-enable-per-device-configs-switch"
            label="GitOps Edge configurations"
            labelClass="col-sm-3 col-lg-2"
            tooltip="By enabling the GitOps Edge Configurations feature, you gain the ability to use dynamic variables in your Helm values file paths. Portainer will then automatically fetch the content from your git repository by matching the folder name or file name with the Portainer Edge ID or Edge Group name, and apply it to the environment where the stack is deployed"
            checked={value.SupportPerDeviceConfigs}
            onChange={(checked) =>
              handleChange({
                SupportPerDeviceConfigs: checked,
                // Helm doesn't use relative path volumes, so don't set SupportRelativePath
              })
            }
          />
        </div>
      </div>

      {value.SupportPerDeviceConfigs && (
        <>
          <InsightsBox
            content={
              <div>
                <p>
                  Files or folders named with either{' '}
                  <code>$&#123;PORTAINER_EDGE_ID&#125;</code> or{' '}
                  <code>$&#123;PORTAINER_EDGE_GROUP&#125;</code> can be used in
                  your Helm values file paths. Only one matching rule can be
                  active at a time.
                </p>
                <p className="mb-0">
                  <strong>Example paths:</strong>
                </p>
                <ul className="mb-0">
                  <li>
                    <code>values/$&#123;PORTAINER_EDGE_GROUP&#125;.yaml</code> -
                    File matching per group
                  </li>
                  <li>
                    <code>
                      values/$&#123;PORTAINER_EDGE_GROUP&#125;/values.yaml
                    </code>{' '}
                    - Folder matching per group
                  </li>
                  <li>
                    <code>values/$&#123;PORTAINER_EDGE_ID&#125;.yaml</code> -
                    File matching per device
                  </li>
                  <li>
                    <code>
                      values/$&#123;PORTAINER_EDGE_ID&#125;/values.yaml
                    </code>{' '}
                    - Folder matching per device
                  </li>
                </ul>
              </div>
            }
            header="GitOps Edge Configurations"
            insightCloseId="helm-per-device-config-info"
            className="mb-3"
          />

          <div className="form-group">
            <div className="col-sm-12">
              <TextTip color="blue">
                Select the directory in your Git repository containing the
                configuration files or folders, then choose either device
                matching (per Edge ID) or group matching (per Edge Group).
              </TextTip>
            </div>
          </div>

          <FormControl
            label="Configuration path"
            errors={errors.helmRelativePath?.PerDeviceConfigsPath}
            required
            tooltip="Select the directory in your Git repository that contains the configuration files or folders for per-device/group matching."
          >
            <PathSelector
              value={value.PerDeviceConfigsPath}
              onChange={(path) => handleChange({ PerDeviceConfigsPath: path })}
              placeholder="Directory"
              model={gitModel}
              dirOnly
              allowEmpty={false}
              inputId="helm-per-device-config-path"
              hasError={!!errors.helmRelativePath?.PerDeviceConfigsPath}
            />
          </FormControl>

          <div className="form-group">
            <div className="col-sm-12">
              <FormControl label="Device matching rule">
                <Select
                  value={value.PerDeviceConfigsMatchType}
                  data-cy="per-device-configs-match-type-select"
                  disabled={!!value.PerDeviceConfigsGroupMatchType}
                  onChange={(e) =>
                    handleChange({
                      PerDeviceConfigsMatchType: getPerDevConfigsFilterType(
                        e.target.value
                      ),
                    })
                  }
                  options={[
                    {
                      label: '',
                      value: '',
                    },
                    {
                      label: 'Match file name with Portainer Edge ID',
                      value: 'file',
                    },
                    {
                      label: 'Match folder name with Portainer Edge ID',
                      value: 'dir',
                    },
                  ]}
                />
              </FormControl>
            </div>
          </div>

          <div className="form-group">
            <div className="col-sm-12">
              <FormControl label="Group matching rule">
                <Select
                  value={value.PerDeviceConfigsGroupMatchType}
                  data-cy="per-device-configs-group-match-type-select"
                  disabled={!!value.PerDeviceConfigsMatchType}
                  onChange={(e) =>
                    handleChange({
                      PerDeviceConfigsGroupMatchType:
                        getPerDevConfigsFilterType(e.target.value),
                    })
                  }
                  options={[
                    {
                      label: '',
                      value: '',
                    },
                    {
                      label: 'Match file name with Edge Group',
                      value: 'file',
                    },
                    {
                      label: 'Match folder name with Edge Group',
                      value: 'dir',
                    },
                  ]}
                />
              </FormControl>
            </div>
          </div>
        </>
      )}
    </>
  );
}
